/**
 * Platform-level feature toggles.
 *
 * To re-enable a feature, flip the boolean to `true` and verify the
 * relevant UI surfaces and read sites pick it up.
 */

/**
 * Auto-advance: after a slide's narration completes, automatically advance
 * to the next slide after a short cancellable countdown.
 *
 * Disabled platform-wide 2026-05-21 to shrink the test matrix; re-enabled
 * 2026-08-04 after the gaps that made it unreliable were closed:
 *   - Editor preview no longer bypasses the advance gate (it fired
 *     setCurrentIdx directly, skipping unanswered quizzes).
 *   - Both players fire through `goNextRef`, so the timeout sees committed
 *     state. Previously the captured closure made skip=No courses show a
 *     countdown that never advanced — on every format, not just gated ones.
 *   - Six formats veto it outright (AUTO_ADVANCE_EXCLUDED_FORMATS): the five
 *     gated interactions + pdf-viewer, where the learner still has work to do
 *     when the narration stops.
 *   - SCORM reached only 7 of 25 formats (click-mode gates only); the linear
 *     narration-end hook in `playAudioFrom` now covers the rest.
 *   - Video-mode Big Statement advances on the video's `ended` event, which it
 *     could never do before (video mode skips narration entirely).
 *
 * UI affordances (Narration Drawer toggle, Course Default Settings toggle) and
 * every runtime read site remain gated on this flag, so it is still a clean
 * kill switch.
 */
export const FEATURE_AUTO_ADVANCE = true

/**
 * Skip narration button (separate from Next). Hidden 2026-05-21 —
 * the universal Skip rule means Next now performs the slide-skip
 * job when Learner-can-skip = Yes. The button's `skipNarration()` /
 * `stopNarration` functions stay in code in case future UX needs
 * "stop VO, stay on slide" without leaving.
 */
export const FEATURE_NARRATION_SKIP_BUTTON = false

/**
 * Role Play (codename: Dialogue) — production-readiness gate (2026-05-23).
 *
 * Role Play is not yet ready for general availability on creator.learnbee.ai
 * (the production host). The implementation is intact across DialogueListPage,
 * DialogueWizardPage, DialogueSessionsPage, DialoguePlayPage, the schema in
 * Supabase, and the routes in App.tsx. We just hide it from view.
 *
 * Two-axis gate:
 *   - UI visibility (the homepage tile, "+ New" menu option, recent-Role-
 *     Plays section, search): OFF on BOTH dev and creator. Internal team
 *     reaches dev via direct URL only.
 *   - Route mounting (whether /roleplays, /roleplay/*, /play-roleplay/:code
 *     resolve to their pages or silently redirect to /): mounted on dev,
 *     blocked on creator.
 *
 * When Role Play ships:
 *   1. Flip FEATURE_ROLE_PLAY_UI_VISIBLE to true.
 *   2. Drop the hostname check on FEATURE_ROLE_PLAY_ROUTES_ENABLED
 *      (just `export const … = true`).
 *
 * SSR safety: the typeof-window guard avoids ReferenceError if any code
 * path ever evaluates this during a Node-side SSR run (we don't SSR the
 * homepage, but cheap to be defensive).
 */
const _hostname = typeof window !== 'undefined' ? window.location.hostname : ''
const _isProdHost = _hostname === 'creator.learnbee.ai'

export const FEATURE_ROLE_PLAY_UI_VISIBLE = false
export const FEATURE_ROLE_PLAY_ROUTES_ENABLED = !_isProdHost

/**
 * OpenAI Realtime — English RT Beta experiment (2026-06-03).
 *
 * Gates the new voice-transport path that replaces the Whisper →
 * customerTurn → TTS-1 pipeline with a single OpenAI Realtime
 * WebSocket. Activates only when ALL of these hold:
 *   - This flag is true (hostname-gated: dev only by default)
 *   - dialogue.language === 'en-rt' (i.e. the "English (Realtime —
 *     experimental)" language entry in src/lib/languages.ts)
 *   - dialogue.mode === 'customer_led'
 *   - dialogue.chat === false (text-chat surfaces don't use voice
 *     services at all; Realtime is irrelevant there)
 *
 * Full sprint spec lives in ROLE_PLAY_ENGLISH_RT_BETA.md at the repo root.
 *
 * When the experiment is validated and ready for production rollout:
 *   1. Drop the hostname check below (just `export const … = true`)
 *   2. Optionally fold `en-rt` back into the regular `en` entry, or
 *      keep them as separate explicit author choices
 */
export const FEATURE_OPENAI_REALTIME = !_isProdHost


/**
 * Organisations v1 (docs/engineering/ORGANISATIONS_PLAN.md).
 *
 * Everything org-related in the UI — the switcher, "Create organisation",
 * Organisation settings and the invite page `/org/join` — sits behind this.
 * Hostname-gated like Role Play: on for dev.learnbee.ai and localhost, OFF on
 * creator.learnbee.ai even if the code reaches `main`. The endpoints refuse
 * the production host on their own as well (api/orgs/[action].ts), and
 * production Clerk has Organizations switched off, so nothing org-related can
 * happen there.
 */
export const FEATURE_ORGANISATIONS = !_isProdHost
